import { useEffect, useRef, useState, useCallback } from "react";
import { invalidateCache } from "./useCachedQuery";

export type NetworkStatus = "online" | "offline" | "recovering";

interface NetworkState {
  status: NetworkStatus;
  online: boolean;
  effectiveType: string | null;
  rtt: number | null;
  downlink: number | null;
  usingCache: boolean;
  lastSyncAt: number | null;
}

let GLOBAL_STATE: NetworkState = {
  status: "online",
  online: true,
  effectiveType: null,
  rtt: null,
  downlink: null,
  usingCache: false,
  lastSyncAt: null,
};

const LISTENERS = new Set<(state: NetworkState) => void>();

function notifyListeners() {
  LISTENERS.forEach((cb) => cb(GLOBAL_STATE));
}

function updateState(partial: Partial<NetworkState>) {
  GLOBAL_STATE = { ...GLOBAL_STATE, ...partial };
  notifyListeners();
}

function readConnectionInfo() {
  const conn = (navigator as Record<string, unknown>).connection as Record<string, unknown> | undefined;
  if (!conn) return;
  updateState({
    effectiveType: (conn.effectiveType as string) || null,
    rtt: (conn.rtt as number) || null,
    downlink: (conn.downlink as number) || null,
  });
}

function setOffline() {
  updateState({ status: "offline", online: false, usingCache: true });
}

function setOnline() {
  updateState({ status: "recovering", online: true });
  setTimeout(() => {
    if (GLOBAL_STATE.online) {
      updateState({ status: "online", usingCache: false, lastSyncAt: Date.now() });
    }
  }, 1200);
}

function setUsingCache(v: boolean) {
  if (GLOBAL_STATE.usingCache !== v) {
    updateState({ usingCache: v });
  }
}

let INIT_DONE = false;
function initNetworkListeners() {
  if (INIT_DONE) return;
  INIT_DONE = true;

  window.addEventListener("online", setOnline);
  window.addEventListener("offline", setOffline);

  const conn = (navigator as Record<string, unknown>).connection as Record<string, unknown> & { addEventListener?: (event: string, cb: () => void) => void } | undefined;
  if (conn?.addEventListener) {
    conn.addEventListener("change", readConnectionInfo);
    readConnectionInfo();
  }

  const HEARTBEAT_URL = "https://www.google.com/favicon.ico?_=";
  setInterval(() => {
    if (!navigator.onLine) {
      setOffline();
      return;
    }
    const img = new Image();
    const start = Date.now();
    img.src = `${HEARTBEAT_URL}${Date.now()}`;
    img.onload = () => {
      if (Date.now() - start > 5000) {
        updateState({ status: "recovering" });
      } else {
        setOnline();
      }
    };
    img.onerror = () => {
      if (!navigator.onLine) setOffline();
    };
  }, 15000);
}

export function useNetworkStatus(): NetworkState {
  const [state, setState] = useState<NetworkState>(GLOBAL_STATE);

  useEffect(() => {
    initNetworkListeners();
    LISTENERS.add(setState);
    setState(GLOBAL_STATE);
    return () => {
      LISTENERS.delete(setState);
    };
  }, []);

  return state;
}

export function markServingCache(using: boolean) {
  setUsingCache(using);
}

export function resetNetworkCacheState() {
  updateState({
    status: "online",
    online: true,
    usingCache: false,
    lastSyncAt: Date.now(),
  });
  invalidateCache();
}